/**
 * Mobile search (sections/mobile-search.liquid)
 * <mobile-search>
 *  - open() / close(): bottom sheet with a circular reveal; Escape, the round close button and the dimmed
 *    page close it. Opened by the Mobile bottom bar's Search button (window.themeMobileSearch.open()
 *    or the 'theme:mobile-search:open' event).
 *  - Typing shows product suggestions from Shopify predictive search, rendered by this same section
 *    (?q=…&section_id=…), debounced, with stale requests cancelled.
 *  - Enter submits to the normal search page.
 */
(() => {
  if (customElements.get('mobile-search')) return;

  const DEBOUNCE_MS = 250;

  class MobileSearch extends HTMLElement {
    #controller = new AbortController();
    /** @type {AbortController | null} */
    #fetchController = null;
    #timer = 0;
    /** @type {Element | null} */
    #opener = null;
    /** @type {Map<string, string>} */
    #cache = new Map();

    connectedCallback() {
      const { signal } = this.#controller;
      const input = this.#input();

      this.querySelector('[data-mobile-search-close]')?.addEventListener('click', () => this.close(), { signal });
      // Tap on the dimmed page (outside the sheet) closes
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }, { signal });

      input?.addEventListener('input', () => {
        clearTimeout(this.#timer);
        this.#timer = window.setTimeout(() => this.#search(input.value), DEBOUNCE_MS);
      }, { signal });

      this.querySelector('[data-mobile-search-reset]')?.addEventListener('click', () => {
        window.setTimeout(() => {
          this.#hideResults();
          input?.focus();
        }, 0);
      }, { signal });

      document.addEventListener('theme:mobile-search:open', () => this.open(), { signal });
      window.themeMobileSearch = this;
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      this.#fetchController?.abort();
      clearTimeout(this.#timer);
      if (window.themeMobileSearch === this) delete window.themeMobileSearch;
    }

    open() {
      const sheet = this.#sheet();
      if (!sheet) return;
      this.#opener = document.activeElement;
      sheet.inert = false;
      this.classList.add('is-open');
      document.documentElement.classList.add('mobile-search-open');
      // Focus right away so the phone keyboard opens with the tap
      this.#input()?.focus({ preventScroll: true });
    }

    close() {
      const sheet = this.#sheet();
      if (!sheet) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('mobile-search-open');
      this.#input()?.blur();
      window.setTimeout(() => {
        if (!this.classList.contains('is-open')) sheet.inert = true;
      }, 600);
      if (this.#opener instanceof HTMLElement) this.#opener.focus({ preventScroll: true });
    }

    #sheet() {
      return this.querySelector('[data-mobile-search-sheet]');
    }

    /** @returns {HTMLInputElement | null} */
    #input() {
      return this.querySelector('[data-mobile-search-input]');
    }

    /** @param {string} value */
    async #search(value) {
      const terms = value.trim();
      if (!terms) {
        this.#hideResults();
        return;
      }

      const cached = this.#cache.get(terms);
      if (cached) {
        this.#showResults(cached);
        return;
      }

      this.#fetchController?.abort();
      this.#fetchController = new AbortController();
      this.#setLoading(true);
      try {
        const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
        url.searchParams.set('q', terms);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', this.dataset.sectionId || '');
        const response = await fetch(url, { signal: this.#fetchController.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[data-mobile-search-results]');
        if (!results) throw new Error('no results markup');
        this.#cache.set(terms, results.innerHTML);
        // Ignore answers for an older query
        if (this.#input()?.value.trim() === terms) this.#showResults(results.innerHTML);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[mobile-search] suggestions failed', error);
          this.#hideResults();
        }
      } finally {
        this.#setLoading(false);
      }
    }

    /** @param {string} markup */
    #showResults(markup) {
      const dropdown = this.querySelector('[data-mobile-search-dropdown]');
      if (!dropdown) return;
      dropdown.innerHTML = markup;
      dropdown.hidden = false;
      this.#input()?.setAttribute('aria-expanded', 'true');
    }

    #hideResults() {
      const dropdown = this.querySelector('[data-mobile-search-dropdown]');
      if (!dropdown) return;
      dropdown.hidden = true;
      dropdown.replaceChildren();
      this.#input()?.setAttribute('aria-expanded', 'false');
    }

    /** @param {boolean} on */
    #setLoading(on) {
      const loading = this.querySelector('[data-mobile-search-loading]');
      if (loading instanceof HTMLElement) loading.hidden = !on;
    }
  }

  customElements.define('mobile-search', MobileSearch);
})();
